import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  CircularProgress,
  InputAdornment,
  Box,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { useClients } from "../../hooks/useClients";
import { useTypes } from "../../hooks/useTypes";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useCompanies } from "../../hooks/useCompanies";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { checkOpenConversationsByPhone, fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";
import {
  phonesLikelyMatch,
  toE164Digits,
  formatStoredPhoneForInput,
  phoneDigitsOnly,
} from "../../utils/phone";

const digitsOnly = phoneDigitsOnly;

const isChatBotUser = (u) => Number(u?.ura) === 1;

function flattenTypesTree(nodes, parentId = null) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : parentId;
    const nome = n.nome ?? n.name ?? String(id);
    const { children: _c, ...rest } = n;
    const item = { ...rest, id: Number(id), id_pai: idPai, nome };
    return [item, ...flattenTypesTree(n.children || [], Number(id))];
  });
}

function getSelectedCompanyIds() {
  try {
    const raw = sessionStorage.getItem("selectedCompanies");
    if (raw == null || raw === "" || raw === "undefined") return [];
    let arr = [];
    try { arr = JSON.parse(raw); } catch {
      const fromCsv = String(raw).split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n) && n > 0);
      if (fromCsv.length) return fromCsv;
      return [];
    }
    if (!Array.isArray(arr)) return [];
    return arr.map((id) => Number(id)).filter((n) => !Number.isNaN(n) && n > 0);
  } catch { return []; }
}

/**
 * Dialog to promote a personal WhatsApp conversation to a formal request.
 * Follows the same field patterns as NewRequestModal (client, type, subtypes, advanced options)
 * and adds message selection.
 */
const PromoteToRequestDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  currentUser,
  messages,
  peerPhone,
  peerDisplayName = "",
  peerAvatar = null,
  error: externalError,
}) => {
  const { t } = useTranslation("chat");

  // Client fields
  const [clientMode, setClientMode] = useState("existing");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");

  // Type/subtypes
  const [typeId, setTypeId] = useState("");
  const [subtype1, setSubtype1] = useState("");
  const [subtype2, setSubtype2] = useState("");
  const [subtype3, setSubtype3] = useState("");

  // Advanced
  const [channelId, setChannelId] = useState("");
  const [assigneeType, setAssigneeType] = useState("user");
  const [assigneeId, setAssigneeId] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Message selection
  const [selectedMsgIds, setSelectedMsgIds] = useState(() => new Set());

  // Duplicate phone warning
  const [isCheckingOpenByPhone, setIsCheckingOpenByPhone] = useState(false);
  const [openPhoneWarning, setOpenPhoneWarning] = useState(false);
  const [openPhoneWarningItems, setOpenPhoneWarningItems] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);

  const [localError, setLocalError] = useState("");

  const selectedCompanyIds = open ? getSelectedCompanyIds() : [];
  const hasOneCompany = selectedCompanyIds.length === 1;
  const hasMultipleCompanies = selectedCompanyIds.length > 1;
  const hasNoCompany = selectedCompanyIds.length === 0;

  const effectiveProjectId =
    hasOneCompany ? Number(selectedCompanyIds[0])
    : hasMultipleCompanies && selectedProjectId ? Number(selectedProjectId)
    : null;

  // Telefone do contato (chatKey) deve aparecer no form mesmo antes de escolher empresa no multi-projeto.
  useEffect(() => {
    if (!open) return;
    const digits = digitsOnly(peerPhone);
    if (!digits) return;
    const display = formatStoredPhoneForInput(peerPhone) || peerPhone;
    setNewClientPhone((prev) => {
      const prevDigits = digitsOnly(prev);
      if (!prev.trim() || prevDigits === digits) return display;
      return prev;
    });
  }, [open, peerPhone]);

  // Localizar cliente pelo telefone ao abrir (mesmo projeto) e pré-preencher cadastro novo se não achar
  useEffect(() => {
    if (!open || !peerPhone || !effectiveProjectId) return;
    if (hasNoCompany) return;
    if (hasMultipleCompanies && !selectedProjectId) return;

    let cancelled = false;
    (async () => {
      try {
        const idsParam = encodeURIComponent(String(effectiveProjectId));
        const searchTerm = toE164Digits(peerPhone, "BR") || digitsOnly(peerPhone) || peerPhone;
        const r = await fetchData(
          `/clients?page=1&limit=50&search=${encodeURIComponent(searchTerm)}&ids_projetos=${idsParam}`
        );
        const list = Array.isArray(r?.data) ? r.data : [];
        const match = list.find((c) => phonesLikelyMatch(peerPhone, c));
        if (cancelled) return;
        if (match) {
          setClientMode("existing");
          setSelectedClient(match);
          setClientSearch(match.nome || match.name || peerPhone);
        } else {
          setClientMode("new");
          setSelectedClient(null);
          setClientSearch(peerPhone);
          setNewClientPhone(formatStoredPhoneForInput(peerPhone) || peerPhone);
          const nm = String(peerDisplayName || "").trim();
          setNewClientName(nm && !/^\d+$/.test(nm.replace(/\s/g, "")) ? nm : "");
        }
      } catch {
        if (cancelled) return;
        setClientMode("new");
        setSelectedClient(null);
        setClientSearch(peerPhone);
        setNewClientPhone(formatStoredPhoneForInput(peerPhone) || peerPhone);
        const nm = String(peerDisplayName || "").trim();
        setNewClientName(nm && !/^\d+$/.test(nm.replace(/\s/g, "")) ? nm : "");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, peerPhone, effectiveProjectId, hasNoCompany, hasMultipleCompanies, selectedProjectId]);

  useEffect(() => {
    if (!open || clientMode !== "new") return;
    const nm = String(peerDisplayName || "").trim();
    if (!nm) return;
    if (/^\d+$/.test(nm.replace(/\s/g, ""))) return;
    setNewClientName((prev) => (prev && prev.trim() ? prev : nm));
  }, [open, clientMode, peerDisplayName]);

  // Data hooks
  const debouncedSearch = useDebouncedValue(clientSearch, 400);
  const { data: clientsData, isLoading: loadingClients } = useClients(
    1, 15, debouncedSearch.trim(), effectiveProjectId ?? undefined,
    {
      enabled: !!open && clientMode === "existing" &&
        typeof debouncedSearch === "string" && debouncedSearch.trim().length >= 2 &&
        (!hasMultipleCompanies || !!selectedProjectId),
    }
  );
  const clients = clientsData?.clients ?? [];

  const { data: companiesData } = useCompanies(1, 100, "");
  const allCompanies = companiesData?.companies ?? [];
  const companiesForSelect = useMemo(
    () => allCompanies.filter((c) => selectedCompanyIds.includes(Number(c.id))),
    [allCompanies, selectedCompanyIds]
  );
  const companySelectOptions = useMemo(() => {
    if (companiesForSelect.length > 0)
      return companiesForSelect.map((c) => ({ id: Number(c.id), label: c.nome ?? c.name ?? `Empresa ${c.id}` }));
    return selectedCompanyIds.map((id) => ({ id, label: `Empresa ${id}` }));
  }, [companiesForSelect, selectedCompanyIds]);

  const { data: typesData } = useTypes(1, 500, "");
  const typesFlat = useMemo(() => flattenTypesTree(typesData?.types ?? []), [typesData?.types]);
  const rootTypes = useMemo(() => typesFlat.filter((t) => t.id_pai == null || t.id_pai === 0), [typesFlat]);
  const subtype1Options = useMemo(() => typeId ? typesFlat.filter((t) => Number(t.id_pai) === Number(typeId)) : [], [typesFlat, typeId]);
  const subtype2Options = useMemo(() => subtype1 ? typesFlat.filter((t) => Number(t.id_pai) === Number(subtype1)) : [], [typesFlat, subtype1]);
  const subtype3Options = useMemo(() => subtype2 ? typesFlat.filter((t) => Number(t.id_pai) === Number(subtype2)) : [], [typesFlat, subtype2]);

  const { data: channelsData } = useChannels(1, 200, "");
  const channels = channelsData?.channels ?? [];

  const { data: usersData } = useUsers(1, 500, "", "todos");
  const users = usersData?.users ?? [];
  const { data: agentsData } = useAgents(1, 200, "");
  const agents = agentsData?.agents ?? [];
  const currentUserId = currentUser?.id ?? null;

  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  useEffect(() => {
    if (open && currentUserId && assigneeType === "user" && !assigneeId) setAssigneeId(String(currentUserId));
  }, [open, currentUserId, assigneeType, assigneeId]);

  useEffect(() => {
    if (open && hasMultipleCompanies && selectedProjectId && !selectedCompanyIds.includes(Number(selectedProjectId)))
      setSelectedProjectId("");
  }, [open, hasMultipleCompanies, selectedProjectId, selectedCompanyIds]);

  const prevSelectedProjectIdRef = React.useRef(selectedProjectId);
  useEffect(() => {
    if (hasMultipleCompanies && prevSelectedProjectIdRef.current !== selectedProjectId) {
      prevSelectedProjectIdRef.current = selectedProjectId;
      setSelectedClient(null);
      setClientSearch(peerPhone || "");
    } else if (hasMultipleCompanies) {
      prevSelectedProjectIdRef.current = selectedProjectId;
    }
  }, [hasMultipleCompanies, selectedProjectId, peerPhone]);

  useEffect(() => { if (!typeId) { setSubtype1(""); setSubtype2(""); setSubtype3(""); } }, [typeId]);
  useEffect(() => { if (!subtype1) setSubtype2(""); if (!subtype2) setSubtype3(""); }, [subtype1, subtype2]);

  const handleClose = () => {
    if (isLoading) return;
    setClientMode("existing");
    setClientSearch("");
    setSelectedClient(null);
    setNewClientName("");
    setNewClientEmail("");
    setNewClientPhone("");
    setTypeId("");
    setSubtype1("");
    setSubtype2("");
    setSubtype3("");
    setChannelId("");
    setAssigneeType("user");
    setAssigneeId(currentUserId ? String(currentUserId) : "");
    setShowAdvanced(false);
    setSelectedProjectId("");
    setSelectedMsgIds(new Set());
    setLocalError("");
    setIsCheckingOpenByPhone(false);
    setOpenPhoneWarning(false);
    setOpenPhoneWarningItems([]);
    setPendingPayload(null);
    onClose();
  };

  const selectedType = useMemo(
    () => (typeId ? typesFlat.find((t) => Number(t.id) === Number(typeId)) : null),
    [typesFlat, typeId]
  );
  const derivedTypeKind = selectedType?.tipo === 0 ? "0" : "1";

  const toggleMsg = (id) => {
    setSelectedMsgIds((prev) => {
      const next = new Set(prev);
      const k = String(id);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const selectAllMsgs = () => {
    const allIds = sortedMessages.map((m) => String(m.id));
    setSelectedMsgIds(new Set(allIds));
  };

  const sortedMessages = useMemo(() =>
    [...(messages ?? [])]
      .filter((m) => m && m.id != null && !String(m.id).startsWith("temp-"))
      .sort((a, b) => (Number(a.ts) || 0) - (Number(b.ts) || 0)),
    [messages]
  );

  const canSubmit = () => {
    if (hasNoCompany) return false;
    if (hasMultipleCompanies && !selectedProjectId) return false;
    if (selectedMsgIds.size === 0) return false;
    if (clientMode === "existing")
      return !!(selectedClient?.id ?? selectedClient?.id_cliente) && !isLoading;
    const name = (newClientName || "").trim();
    return name.length >= 2 && !isLoading;
  };

  const buildPayload = () => {
    const projectId = hasOneCompany
      ? Number(selectedCompanyIds[0])
      : hasMultipleCompanies && selectedProjectId ? Number(selectedProjectId) : null;

    let clientPayload;
    let clientIdPayload;
    const existingIdRaw = selectedClient?.id ?? selectedClient?.id_cliente;
    if (clientMode === "existing" && existingIdRaw != null && existingIdRaw !== "") {
      const idNum = Number(existingIdRaw);
      if (Number.isFinite(idNum) && idNum > 0) {
        clientIdPayload = idNum;
        clientPayload = {
          id: idNum,
          phone: (() => {
            const raw = (
              selectedClient?.celular ||
              selectedClient?.telefone ||
              newClientPhone ||
              peerPhone ||
              ""
            ).trim();
            if (!raw) return undefined;
            return toE164Digits(raw, "BR") || digitsOnly(raw) || raw;
          })(),
          name: selectedClient?.name || selectedClient?.nome,
          email: selectedClient?.email,
        };
      }
    }
    if (!clientPayload) {
      const name = (newClientName || "").trim() || "Cliente";
      const rawPhone = (newClientPhone || peerPhone || "").trim();
      const phoneOut =
        rawPhone && !/^grupo:/i.test(rawPhone)
          ? toE164Digits(rawPhone, "BR") || digitsOnly(rawPhone) || undefined
          : undefined;
      clientPayload = {
        name,
        email: (newClientEmail || "").trim() || undefined,
        phone: phoneOut,
        telephone: phoneOut,
        ...(peerAvatar && /^https?:\/\//i.test(String(peerAvatar).trim())
          ? { profilePicture: String(peerAvatar).trim() }
          : {}),
        ...(projectId != null && { id_projeto: projectId }),
      };
    }

    return {
      messageIds: [...selectedMsgIds],
      projectId: projectId || undefined,
      client: clientPayload,
      ...(clientIdPayload != null && clientIdPayload !== "" && { clientId: clientIdPayload }),
      typeKind: derivedTypeKind,
      typeId: typeId ? Number(typeId) : undefined,
      subtype1: subtype1 ? Number(subtype1) : undefined,
      subtype2: subtype2 ? Number(subtype2) : undefined,
      subtype3: subtype3 ? Number(subtype3) : undefined,
      channelId: channelId ? Number(channelId) : undefined,
      assignedUserId: assigneeType === "user" && assigneeId ? Number(assigneeId) : undefined,
      assignedAgentId: assigneeType === "agent" && assigneeId ? Number(assigneeId) : undefined,
    };
  };

  const getFormPhoneForCheck = () => {
    if (clientMode === "existing") {
      const c = selectedClient || {};
      return c.phone || c.celular || c.telefone || "";
    }
    return (newClientPhone || "").trim();
  };

  const doSubmit = async (payload) => {
    setLocalError("");
    try {
      await onSubmit(payload);
    } catch (e) {
      const msg =
        (typeof e?.message === "string" && e.message) ||
        (Array.isArray(e?.message) && e.message[0]) ||
        t("pane.promoteError");
      setLocalError(msg);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setLocalError("");

    const payload = buildPayload();
    const phoneToCheck = getFormPhoneForCheck();
    const trimmedPhone = String(phoneToCheck || "").trim();

    if (!trimmedPhone) {
      await doSubmit(payload);
      return;
    }

    try {
      setIsCheckingOpenByPhone(true);
      const projectId = payload.projectId != null ? Number(payload.projectId) : null;
      const existing = await checkOpenConversationsByPhone({
        phone: trimmedPhone,
        projectId: projectId || undefined,
      });
      if (Array.isArray(existing) && existing.length > 0) {
        setOpenPhoneWarningItems(existing);
        setPendingPayload(payload);
        setOpenPhoneWarning(true);
        return;
      }
      await doSubmit(payload);
    } catch {
      await doSubmit(payload);
    } finally {
      setIsCheckingOpenByPhone(false);
    }
  };

  const displayError = localError || externalError || "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("pane.promoteDialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t("pane.promoteDialogHint")}
          </Typography>

          {/* Company selector (multi-company) */}
          {hasNoCompany && (
            <Typography variant="body2" color="error" sx={{ py: 1 }}>
              {t("newRequest.selectCompanyError")}
            </Typography>
          )}
          {hasMultipleCompanies && (
            <FormControl fullWidth size="small" required>
              <InputLabel>{t("newRequest.linkCompany")}</InputLabel>
              <Select
                value={selectedProjectId}
                label={t("newRequest.linkCompany")}
                onChange={(e) => setSelectedProjectId(String(e.target.value ?? ""))}
              >
                <MenuItem value=""><em>{t("newRequest.selectCompany")}</em></MenuItem>
                {companySelectOptions.map((opt) => (
                  <MenuItem key={opt.id} value={String(opt.id)}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Client: existing or new */}
          <Box>
            {hasMultipleCompanies && !selectedProjectId && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("newRequest.selectCompanyForClients")}
              </Typography>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t("newRequest.client")}
            </Typography>
            <RadioGroup row value={clientMode} onChange={(e) => setClientMode(e.target.value)} sx={{ mb: 1.5 }}>
              <FormControlLabel value="existing" control={<Radio size="small" />} label={t("newRequest.searchExisting")} />
              <FormControlLabel value="new" control={<Radio size="small" />} label={t("newRequest.registerNew")} />
            </RadioGroup>

            {clientMode === "existing" ? (
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => typeof option === "object" ? option?.name || option?.nome || "" : ""}
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                inputValue={clientSearch}
                onInputChange={(_, value) => setClientSearch(value)}
                loading={loadingClients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("newRequest.searchClientLabel")}
                    placeholder={t("newRequest.searchClientPlaceholder")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonSearchRoundedIcon color="action" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingClients ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name || option.nome || t("newRequest.noName")}</Typography>
                      {option.email && <Typography variant="caption" color="text.secondary" display="block">{option.email}</Typography>}
                    </Box>
                  </li>
                )}
              />
            ) : (
              <Stack direction="column" spacing={1.5}>
                <TextField
                  label={t("newRequest.clientName")}
                  placeholder={t("newRequest.required")}
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonAddRoundedIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t("newRequest.emailOptional")}
                  type="email"
                  placeholder="exemplo@email.com"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t("newRequest.phoneOptional")}
                  placeholder="(00) 00000-0000"
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(e.target.value)}
                  size="small"
                  fullWidth
                />
                {peerAvatar && /^https?:\/\//i.test(String(peerAvatar).trim()) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                    <Avatar src={String(peerAvatar).trim()} sx={{ width: 40, height: 40 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t("pane.promoteSuggestedPhotoHint")}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            )}
          </Box>

          {/* Type + cascading subtypes */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>{t("newRequest.type")}</InputLabel>
              <Select value={typeId} label={t("newRequest.type")} onChange={(e) => setTypeId(e.target.value)}>
                <MenuItem value="">{t("newRequest.typeNone")}</MenuItem>
                {rootTypes.map((tp) => (
                  <MenuItem key={tp.id} value={tp.id}>{tp.nome ?? tp.name ?? `Tipo ${tp.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {typeId && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {subtype1Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 1</InputLabel>
                    <Select value={subtype1} label="Subtipo 1" onChange={(e) => setSubtype1(e.target.value)}>
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype1Options.map((tp) => <MenuItem key={tp.id} value={tp.id}>{tp.nome ?? tp.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                {subtype2Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 2</InputLabel>
                    <Select value={subtype2} label="Subtipo 2" onChange={(e) => setSubtype2(e.target.value)}>
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype2Options.map((tp) => <MenuItem key={tp.id} value={tp.id}>{tp.nome ?? tp.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                {subtype3Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 3</InputLabel>
                    <Select value={subtype3} label="Subtipo 3" onChange={(e) => setSubtype3(e.target.value)}>
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype3Options.map((tp) => <MenuItem key={tp.id} value={tp.id}>{tp.nome ?? tp.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}
          </Box>

          {/* Advanced options */}
          <Box>
            <Button
              size="small"
              startIcon={showAdvanced ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {showAdvanced ? t("newRequest.advancedHide") : t("newRequest.advancedShow")}
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={2} sx={{ mt: 1.5, pl: 0.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("newRequest.sourceChannel")}</InputLabel>
                  <Select value={channelId} label={t("newRequest.sourceChannel")} onChange={(e) => setChannelId(e.target.value)}>
                    <MenuItem value="">{t("newRequest.defaultChannel")}</MenuItem>
                    {channels.map((ch) => (
                      <MenuItem key={ch.id} value={ch.id}>{ch.nome ?? ch.name ?? `Canal ${ch.id}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("newRequest.assignee")}</InputLabel>
                  <Select
                    value={
                      assigneeType === "queue" ? "queue"
                      : assigneeType === "agent" ? (assigneeId ? `agent-${assigneeId}` : "queue")
                      : assigneeType === "user" ? (assigneeId ? `user-${assigneeId}` : "queue")
                      : "queue"
                    }
                    label={t("newRequest.assignee")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "queue") { setAssigneeType("queue"); setAssigneeId(""); return; }
                      if (String(v).startsWith("agent-")) { setAssigneeType("agent"); setAssigneeId(String(v).replace("agent-", "")); return; }
                      if (String(v).startsWith("user-")) { setAssigneeType("user"); setAssigneeId(String(v).replace("user-", "")); }
                    }}
                    renderValue={(v) => {
                      if (v === "queue") return t("newRequest.queue");
                      if (String(v).startsWith("agent-")) {
                        const id = String(v).replace("agent-", "");
                        const a = agents.find((x) => String(x.id ?? x.id_agente) === id);
                        return a ? (a.name ?? a.nome ?? `Agente ${id}`) : `Agente ${id}`;
                      }
                      const id = String(v).replace("user-", "");
                      const u = users.find((x) => String(x.id) === id);
                      const nome = u ? (u.nome ?? u.name ?? `Usuário ${id}`) : `Usuário ${id}`;
                      const suffix = Number(id) === Number(currentUserId) ? t("newRequest.you") : (u && isChatBotUser(u) ? " (ChatBot)" : "");
                      return nome + suffix;
                    }}
                  >
                    <MenuItem value="queue">{t("newRequest.queue")}</MenuItem>
                    {humanUsers.length > 0 && [
                      <MenuItem key="div-human" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— {t("newRequest.usersDivider")} —</MenuItem>,
                      ...humanUsers.map((u) => (
                        <MenuItem key={`u-${u.id}`} value={`user-${u.id}`}>
                          {u.nome ?? u.name ?? `Usuário ${u.id}`}
                          {Number(u.id) === Number(currentUserId) ? t("newRequest.you") : ""}
                        </MenuItem>
                      )),
                    ]}
                    {chatbotUsers.length > 0 && [
                      <MenuItem key="div-chatbot" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— ChatBots —</MenuItem>,
                      ...chatbotUsers.map((u) => (
                        <MenuItem key={`cb-${u.id}`} value={`user-${u.id}`}>
                          {u.nome ?? u.name ?? `ChatBot ${u.id}`} (ChatBot)
                        </MenuItem>
                      )),
                    ]}
                    {agents.length > 0 && [
                      <MenuItem key="div-agent" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— {t("newRequest.aiAgentsDivider")} —</MenuItem>,
                      ...agents.map((a) => {
                        const id = a.id ?? a.id_agente ?? a.agentId;
                        return (
                          <MenuItem key={`a-${id}`} value={`agent-${id}`}>
                            {a.name ?? a.nome ?? `Agente ${id}`}
                          </MenuItem>
                        );
                      }),
                    ]}
                  </Select>
                </FormControl>
              </Stack>
            </Collapse>
          </Box>

          <Divider />

          {/* Message selection */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("pane.promotePickMessages")}
              </Typography>
              {sortedMessages.length > 0 && (
                <Button size="small" onClick={selectAllMsgs} sx={{ textTransform: "none" }}>
                  {selectedMsgIds.size === sortedMessages.length ? t("pane.promoteCancel") : "Selecionar todas"}
                </Button>
              )}
            </Box>
            <List dense sx={{ maxHeight: 220, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
              {sortedMessages.map((m) => {
                const idStr = String(m.id);
                const label =
                  (m.text || m.mensagem || "").slice(0, 120) ||
                  (m.type && m.type !== "text" ? `[${m.type}]` : "—");
                return (
                  <ListItem key={idStr} dense onClick={() => toggleMsg(idStr)} sx={{ cursor: "pointer" }}>
                    <Checkbox edge="start" checked={selectedMsgIds.has(idStr)} tabIndex={-1} disableRipple />
                    <ListItemText
                      primary={label}
                      secondary={m.direction === "out" || m.senderType === "agent" || m.senderType === "ai_agent" || m.senderType === "chatbot" ? "→" : "←"}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {displayError ? (
            <Typography color="error" variant="body2">{displayError}</Typography>
          ) : null}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={!canSubmit() || isCheckingOpenByPhone}
            startIcon={isLoading || isCheckingOpenByPhone ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading || isCheckingOpenByPhone ? t("pane.promoteWorking") : t("pane.promoteSubmit")}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>{t("pane.promoteCancel")}</Button>
      </DialogActions>

      {/* Duplicate phone warning */}
      <Dialog open={openPhoneWarning} onClose={() => !isLoading && setOpenPhoneWarning(false)}>
        <DialogTitle>{t("newRequest.phoneWarnTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("newRequest.phoneWarnIntro", { count: openPhoneWarningItems.length })}
            <strong>{t("newRequest.pending")}</strong>
            {t("newRequest.or")}
            <strong>{t("newRequest.inProgress")}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">{t("newRequest.phoneWarnHint")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenPhoneWarning(false); setPendingPayload(null); }} disabled={isLoading}>
            {t("newRequest.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              const payload = pendingPayload || buildPayload();
              setOpenPhoneWarning(false);
              setPendingPayload(null);
              await doSubmit(payload);
            }}
            disabled={isLoading}
          >
            {t("newRequest.createAnyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PromoteToRequestDialog;
